import type { AiChatMessage, CopilotScope } from "@productlane/lib/src/ai"
import type { PersistedEnrichments } from "@productlane/lib/src/ai-chat-sync"
import {
	copilotScopeIdentity,
	readPersistedImages,
} from "@productlane/lib/src/ai"

import { env } from "@/env"

// Only thread scope carries a persisted threadId; editor/workspace scopes
// create thread-less sessions.
const threadIdForScope = (scope: CopilotScope): string | null =>
	scope.kind === "thread" ? scope.threadId : null

const API_BASE = `${env.VITE_API_URL}/api/sidebar/ai-chat`

export interface RecentChatSession {
	id: string
	createdAt: string
	updatedAt: string
	messageCount: number
	preview: string | null
}

interface RawHistoryMessage {
	id: string
	role: string
	content: string
	createdAt: string
	enrichments?: PersistedEnrichments | null
}

export const sidebarAiChatApi = {
	createSession: async (options?: {
		fresh?: boolean
		scope?: CopilotScope
	}): Promise<string> => {
		const response = await fetch(`${API_BASE}/session`, {
			method: "POST",
			credentials: "include",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				fresh: options?.fresh ?? false,
				threadId: options?.scope ? threadIdForScope(options.scope) : null,
				...(options?.scope ? copilotScopeIdentity(options.scope) : {}),
			}),
		})

		if (!response.ok) {
			throw new Error("Failed to create AI chat session")
		}

		const data = (await response.json()) as { sessionId: string }
		return data.sessionId
	},

	// Like createSession, but doesn't provision a new row when none exists.
	// Returns the thread's most recent session id, or null. Used when a thread
	// opens so we don't create empty sessions for threads the user never
	// chats about.
	findExistingSession: async (
		threadId: string | null,
	): Promise<string | null> => {
		const response = await fetch(`${API_BASE}/session`, {
			method: "POST",
			credentials: "include",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ lookupOnly: true, threadId }),
		})

		if (!response.ok) {
			throw new Error("Failed to look up AI chat session")
		}

		const data = (await response.json()) as { sessionId: string | null }
		return data.sessionId
	},

	loadHistory: async (sessionId: string): Promise<AiChatMessage[]> => {
		const response = await fetch(`${API_BASE}/${sessionId}`, {
			credentials: "include",
		})

		if (!response.ok) {
			throw new Error("Failed to load AI chat history")
		}

		const data = (await response.json()) as { messages: RawHistoryMessage[] }
		return data.messages.map((msg) => ({
			id: msg.id,
			role: msg.role === "USER" ? ("user" as const) : ("assistant" as const),
			content: msg.content,
			processed: true,
			sources: [],
			actions: [],
			createdAt: new Date(msg.createdAt),
			...(msg.enrichments ?? {}),
			// The raw enrichment JSON is untyped; only a validated images map may
			// reach the renderer.
			images: readPersistedImages(msg.enrichments),
		}))
	},

	listRecentSessions: async (
		threadId: string | null,
	): Promise<RecentChatSession[]> => {
		const query = threadId ? `?threadId=${encodeURIComponent(threadId)}` : ""
		const response = await fetch(`${API_BASE}/sessions${query}`, {
			credentials: "include",
		})

		if (!response.ok) {
			throw new Error("Failed to list AI chat sessions")
		}

		const data = (await response.json()) as { sessions: RecentChatSession[] }
		return data.sessions
	},
}
